import { memo, useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Tooltip } from "antd";
import { Link } from "react-router-dom";
import useSocket from "../../../hooks/useSocket";
import { getApiRequest } from "../../../services/getApiRequest";
import { END_POINTS } from "../../../Helper/Constant";
import goatFrame from "../../../assets/icons/goatAvaterImage.png";
import clownFrame from "../../../assets/icons/clown-icon.png";
import vipIcon from "../../../assets/images/vip-icon.png";
import namePlate from "../../../assets/images/name-plate.svg";
import clownNamePlate from "../../../assets/images/name-plate.png";
import { getAvatarFrameType, getClownGifts, getGoatGifts } from "../../avatarFrame";
import "./VipCarousel.scss";

const DEFAULT_AVATAR =
  "https://fapi.fanzaty.net/api/v1/avtar/get-image/boy3.svg";
const DRAG_THRESHOLD = 16;
const VIP_PAGE_LIMIT = 10;

const getVipUserId = (user) =>
  user?._id || user?.id || user?.userId || user?.user?._id || user?.user?.id;

const getVipUserName = (user) =>
  user?.userName ||
  user?.firstName ||
  user?.nickName ||
  user?.user?.userName ||
  "";

const getVipAvatar = (user) =>
  user?.avatarUrl && user.avatarUrl !== "null"
    ? user.avatarUrl
    : user?.user?.avatarUrl && user.user.avatarUrl !== "null"
      ? user.user.avatarUrl
      : DEFAULT_AVATAR;

const NAME_ARC_PATH = "M17.2 10.2 C 28.4 14.1, 53.6 14.1, 64.8 10.2";
const CLOWN_NAME_ARC_PATH = "M16.4 11.4 C 28.2 14.6, 53.8 14.6, 65.6 11.4";
const NAME_ARC_MAX_WIDTH = 42;

const getVipNameFontFamily = () =>
  typeof document !== "undefined" &&
  document.body.classList.contains("font-arabic")
    ? 'Alexandria, "Segoe UI", sans-serif'
    : 'Anybody, "Segoe UI", sans-serif';

const fitNameToArc = (name, maxWidth = NAME_ARC_MAX_WIDTH) => {
  if (!name) return "";
  if (typeof document === "undefined") {
    return name.length > 8 ? `${name.slice(0, 7)}…` : name;
  }
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) return name.length > 8 ? `${name.slice(0, 7)}…` : name;
  ctx.font = `700 8px ${getVipNameFontFamily()}`;
  if (ctx.measureText(name).width <= maxWidth) return name;
  const ellipsis = "…";
  const ellipsisWidth = ctx.measureText(ellipsis).width;
  let truncated = name;
  while (
    truncated.length > 0 &&
    ctx.measureText(truncated).width + ellipsisWidth > maxWidth
  ) {
    truncated = truncated.slice(0, -1);
  }
  return truncated ? `${truncated}${ellipsis}` : ellipsis;
};

const VipNamePlate = ({ name, id, variant = "goat" }) => {
  const pathId = `vip-name-arc-${String(id).replace(/[^a-zA-Z0-9_-]/g, "")}`;
  const isClown = variant === "clown";
  const label = fitNameToArc(name);
  const plate = (
    <span
      className={`vip-carousel__name-wrap${isClown ? " vip-carousel__name-wrap--clown" : ""}`}
    >
      <svg
        className={`vip-carousel__name${isClown ? " vip-carousel__name--clown" : ""}`}
        viewBox="0 0 82 24"
        width="80"
        height="24"
        role="img"
        aria-label={name}
      >
        <image
          href={isClown ? clownNamePlate : namePlate}
          width="82"
          height="24"
        />
        <defs>
          <path
            id={pathId}
            d={isClown ? CLOWN_NAME_ARC_PATH : NAME_ARC_PATH}
            fill="none"
          />
        </defs>
        <text
          fill="#000"
          fontSize="8"
          fontWeight="700"
          fontFamily={getVipNameFontFamily()}
        >
          <textPath
            href={`#${pathId}`}
            startOffset="50%"
            textAnchor="middle"
            method="align"
            spacing="auto"
          >
            {label}
          </textPath>
        </text>
      </svg>
    </span>
  );

  if (!name) return plate;

  return (
    <Tooltip title={name} mouseEnterDelay={0.25}>
      {plate}
    </Tooltip>
  );
};

VipNamePlate.propTypes = {
  name: PropTypes.string.isRequired,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  variant: PropTypes.oneOf(["goat", "clown", "vip"]),
};

const parseVipPayload = (payload) => {
  const data = payload?.data || payload || {};
  const list =
    data.vips ||
    data.vipList ||
    data.users ||
    data.list ||
    (Array.isArray(data.data) ? data.data : null) ||
    (Array.isArray(payload) ? payload : []);
  const users = Array.isArray(list) ? list : [];
  const pagination = data.pagination || payload?.pagination || {};
  const currentPage = pagination.currentPage || pagination.page || 1;
  const totalPages = pagination.totalPages || 1;
  const hasMore =
    pagination.hasMore ??
    (pagination.currentPage != null && pagination.totalPages != null
      ? pagination.currentPage < totalPages
      : users.length >= VIP_PAGE_LIMIT);
  const isCurrentUserVip =
    typeof data.isCurrentUserVip === "boolean"
      ? data.isCurrentUserVip
      : typeof payload?.isCurrentUserVip === "boolean"
        ? payload.isCurrentUserVip
        : undefined;

  return { users, currentPage, hasMore, isCurrentUserVip };
};

const mergeVipUsers = (prev, next) => {
  const seen = new Set(prev.map(getVipUserId).filter(Boolean));
  const added = next.filter((user) => {
    const id = getVipUserId(user);
    if (!id || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
  return added.length ? [...prev, ...added] : prev;
};

const VipCarousel = ({ refreshKey = 0, onCurrentUserVipChange }) => {
  const { socket, isReady } = useSocket();
  const [users, setUsers] = useState([]);
  const pageRef = useRef(1);
  const hasMoreRef = useRef(true);
  const loadingMoreRef = useRef(false);
  const trackRef = useRef(null);
  const dragRef = useRef({
    active: false,
    dragging: false,
    startX: 0,
    scrollLeft: 0,
  });

  const applyVipList = useCallback((payload, append = false) => {
    const parsed = parseVipPayload(payload);
    pageRef.current = parsed.currentPage;
    hasMoreRef.current = parsed.hasMore;
    setUsers((prev) =>
      append ? mergeVipUsers(prev, parsed.users) : parsed.users
    );
    if (typeof parsed.isCurrentUserVip === "boolean") {
      onCurrentUserVipChange?.(parsed.isCurrentUserVip);
    }
    loadingMoreRef.current = false;
  }, [onCurrentUserVipChange]);

  const fetchVipList = useCallback(
    async (page = 1, append = false) => {
      if (append) {
        if (loadingMoreRef.current || !hasMoreRef.current) return;
        loadingMoreRef.current = true;
      }
      try {
        const response = await getApiRequest(
          `${END_POINTS.VIP_LIST}?page=${page}&limit=${VIP_PAGE_LIMIT}`
        );
        if (response && response.success !== false) {
          applyVipList(response, append);
        } else if (append) {
          loadingMoreRef.current = false;
        }
      } catch (error) {
        console.error("Error fetching VIP list:", error);
        loadingMoreRef.current = false;
      }
    },
    [applyVipList]
  );

  useEffect(() => {
    pageRef.current = 1;
    hasMoreRef.current = true;
    fetchVipList(1, false);
  }, [fetchVipList, refreshKey]);

  useEffect(() => {
    if (!socket || !isReady) return;

    const requestVipList = (page = 1) => {
      socket.emit("vip-list", { page, limit: VIP_PAGE_LIMIT });
    };

    const handleVipListUpdated = (payload) => {
      const parsed = parseVipPayload(payload);
      applyVipList(payload, parsed.currentPage > 1);
    };

    const requestFirstPage = () => {
      requestVipList(1);
    };

    socket.on("vip-list-updated", handleVipListUpdated);
    requestFirstPage();
    socket.on("connect", requestFirstPage);

    return () => {
      socket.off("vip-list-updated", handleVipListUpdated);
      socket.off("connect", requestFirstPage);
    };
  }, [socket, isReady, applyVipList, refreshKey]);

  const loadMoreVipUsers = useCallback(() => {
    if (loadingMoreRef.current || !hasMoreRef.current) return;
    const nextPage = pageRef.current + 1;
    fetchVipList(nextPage, true);
    if (socket?.connected) {
      socket.emit("vip-list", { page: nextPage, limit: VIP_PAGE_LIMIT });
    }
  }, [fetchVipList, socket]);

  const getSlideWidth = useCallback(() => {
    const track = trackRef.current;
    if (!track) return 0;
    const item = track.querySelector(".vip-carousel__item");
    if (!item) return 0;
    const styles = window.getComputedStyle(track);
    const gap = Number.parseFloat(styles.columnGap || styles.gap || "0") || 0;
    return item.getBoundingClientRect().width + gap;
  }, []);

  const snapToNearest = useCallback(() => {
    const track = trackRef.current;
    const slideWidth = getSlideWidth();
    if (!track || !slideWidth) return;
    const index = Math.round(track.scrollLeft / slideWidth);
    track.scrollTo({ left: index * slideWidth, behavior: "smooth" });
  }, [getSlideWidth]);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    const onWheel = (event) => {
      const slideWidth = getSlideWidth();
      if (!slideWidth) return;
      event.preventDefault();
      const direction = event.deltaY > 0 || event.deltaX > 0 ? 1 : -1;
      track.scrollBy({ left: direction * slideWidth, behavior: "smooth" });
    };

    const onScroll = () => {
      const threshold = Math.max(getSlideWidth(), 40);
      const nearEnd =
        track.scrollLeft + track.clientWidth >= track.scrollWidth - threshold;
      if (nearEnd) {
        loadMoreVipUsers();
      }
    };

    track.addEventListener("wheel", onWheel, { passive: false });
    track.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      track.removeEventListener("wheel", onWheel);
      track.removeEventListener("scroll", onScroll);
    };
  }, [users.length, getSlideWidth, loadMoreVipUsers]);

  const handlePointerDown = (event) => {
    const track = trackRef.current;
    if (!track || event.pointerType !== "mouse") return;
    dragRef.current = {
      active: true,
      dragging: false,
      startX: event.pageX,
      scrollLeft: track.scrollLeft,
    };
  };

  const handlePointerMove = (event) => {
    const track = trackRef.current;
    if (!track || !dragRef.current.active) return;
    const deltaX = event.pageX - dragRef.current.startX;
    if (Math.abs(deltaX) > DRAG_THRESHOLD) {
      dragRef.current.dragging = true;
    }
    if (dragRef.current.dragging) {
      track.scrollLeft = dragRef.current.scrollLeft - deltaX;
    }
  };

  const handlePointerUp = () => {
    if (dragRef.current.dragging) {
      snapToNearest();
    }
    dragRef.current.active = false;
    window.setTimeout(() => {
      dragRef.current.dragging = false;
    }, 0);
  };

  const handleUserClick = (event) => {
    if (dragRef.current.dragging) {
      event.preventDefault();
    }
  };

  if (!users.length) return null;

  return (
    <div className="vip-carousel">
      <div
        className={`vip-carousel__track${users.length > 3 ? " vip-carousel__track--many" : ""}`}
        ref={trackRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onPointerLeave={handlePointerUp}
      >
        {users.map((user, index) => {
          const userId = getVipUserId(user);
          const name = getVipUserName(user);
          const frameType = getAvatarFrameType(user) || "vip";
          const goatCount = getGoatGifts(user);
          const clownCount = getClownGifts(user);
          const itemClassName = `vip-carousel__item has-${frameType}`;
          const itemContent = (
            <>
              <span className="vip-carousel__avatar-wrap">
                <img
                  src={getVipAvatar(user)}
                  alt={name}
                  className="vip-carousel__avatar"
                  loading="lazy"
                  draggable={false}
                />
                {frameType === "goat" && (
                  <>
                    <img
                      src={goatFrame}
                      alt=""
                      className="vip-carousel__goat"
                      draggable={false}
                    />
                    <span className="vip-carousel__count vip-carousel__count--goat">
                      {goatCount}
                    </span>
                  </>
                )}
                {frameType === "clown" && (
                  <>
                    <img
                      src={clownFrame}
                      alt=""
                      className="vip-carousel__clown"
                      draggable={false}
                    />
                    <span className="vip-carousel__count vip-carousel__count--clown">
                      {clownCount}
                    </span>
                  </>
                )}
                {frameType === "vip" && (
                  <img
                    src={vipIcon}
                    alt=""
                    className="vip-carousel__vip"
                    draggable={false}
                  />
                )}
              </span>
              <VipNamePlate
                name={name}
                id={userId || `${name}-${index}`}
                variant={frameType}
              />
            </>
          );

          if (!userId) {
            return (
              <div key={`${name}-${index}`} className={itemClassName}>
                {itemContent}
              </div>
            );
          }

          return (
            <Link
              key={userId}
              to={`/consumer-Profile/${userId}`}
              className={itemClassName}
              onClick={handleUserClick}
              draggable={false}
            >
              {itemContent}
            </Link>
          );
        })}
      </div>
    </div>
  );
};

VipCarousel.propTypes = {
  refreshKey: PropTypes.number,
  onCurrentUserVipChange: PropTypes.func,
};

export default memo(VipCarousel);
